import { MappedEventTypes } from '@src/common/types/empTypes';
import { getEmptyRuleGroupInAPIForm, isEmptyOrNotEmptyOp } from '@src/emp-3.0/utils';
import { IMappedEvent } from '@src/pages/metric-templates/types';
import { cloneDeep, isEqual } from 'lodash';

/**
 * ### filter out events that have empty event name and empty rules
 *
 * rule format:
 *
 * {
 *   key: eventTypeOfMappedEvent.all_event_name,
 *   op: stringOp.eq,
 *   value: [] as string[],
 *   mapping_rules: [
 *     [
 *       {
 *         tag_key: {
 *           op: stringOp.eq,
 *           value: [],
 *         },
 *         tag_value: {
 *           op: stringOp.eq,
 *           value: [],
 *         },
 *       },
 *     ],
 *   ],
 *   metadata: [],
 * }
 *
 *
 * */
export const trimMappedEventRules = (
  rules: MappedEventTypes['rules'] | IMappedEvent['rules'] = [],
) => {
  // filter out events that have empty event name and empty rules
  // but do not filter out events that have empty event name but metadata
  const clonedRules = cloneDeep(rules);
  const eventRules = clonedRules?.[0] ?? [];

  const res = eventRules.filter((eventRule) => {
    const hasEventName = eventRule?.key?.length > 0 && eventRule?.value?.length > 0;

    // filter out mapping rules that have empty tag key and empty tag value
    const validMappingRules =
      eventRule?.mapping_rules?.filter((ruleItem) => {
        ruleItem = ruleItem.filter((tagRuleItem) => {
          return (
            tagRuleItem.tag_key?.value?.length > 0 ||
            tagRuleItem.tag_value?.value?.length > 0 ||
            isEmptyOrNotEmptyOp(tagRuleItem.tag_value?.op)
          );
        });
        return ruleItem.length > 0;
      }) ?? [];

    // filter out metadata that have empty key
    const validMetadata =
      eventRule?.metadata?.filter((metadataItem) => {
        return (
          metadataItem.filter((orMetadataItem) => {
            return !!(orMetadataItem?.key ?? '');
          }).length !== 0
        );
      }) ?? [];

    eventRule.mapping_rules = [...validMappingRules];
    eventRule.metadata = [...validMetadata];

    // return true if the event name is not empty or there are valid mapping rules
    return hasEventName || validMappingRules.length > 0 || validMetadata.length > 0;
  });

  if (res.length === 0) {
    res.push(getEmptyRuleGroupInAPIForm());
  }
  return [res];
};

export const isEmptyMappedEventRules = (
  rules: MappedEventTypes['rules'] | IMappedEvent['rules'] = [],
): boolean => {
  if (!rules?.length) return true;

  const eventRules = trimMappedEventRules(rules) ?? [];
  if (eventRules.length === 0) return true;

  // Determine whether there is only one empty rules, and this rule is equal to the getEmptyRuleGroupInAPIForm ()
  return eventRules.length === 1 && isEqual(eventRules[0], [getEmptyRuleGroupInAPIForm()]);
};
